import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useDateRange } from "@/context/DateRangeContext";
import { useHeaderStateProvider } from "@/context/HeaderStateContext";
import { useRegion } from "@/context/CountryContext";
import {
  portfolioMetricsService,
  type PortfolioMetricsRequest,
  type PortfolioMetricsResponse,
  type PortfolioMetricsRow,
} from "../services/portfolioMetricsService";

const formatDate = (d: Date) => dayjs(d).format("YYYY-MM-DD");

export const PORTFOLIO_QUERY_KEYS = {
  metrics: "portfolio-metrics",
};

export interface PortfolioKPIMetric {
  title: string;
  value: string;
  change: string;
  changeType: "up" | "down";
  subLabel: string;
}

export interface UsePortfolioMetricsResult {
  kpiData: PortfolioKPIMetric[];
  rows: PortfolioMetricsRow[];
  isLoading: boolean;
  error: Error | null;
}

export function usePortfolioMetrics(portfolioIds?: string[]): UsePortfolioMetricsResult {
  const { dateRange } = useDateRange();
  const { adAccounts } = useHeaderStateProvider();
  const { selectedRegion } = useRegion();
  const location = useLocation();
  const isAdsRoute = location.pathname.toLowerCase().includes("/ads");

  // Track localStorage changes for ads profile selection, mirroring useSPMetrics
  const [localStorageVersion, setLocalStorageVersion] = useState(0);

  useEffect(() => {
    if (!isAdsRoute) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "app_v2_ads_profiles") {
        setLocalStorageVersion((prev) => prev + 1);
      }
    };

    const handleAdsProfileChange = () => {
      setLocalStorageVersion((prev) => prev + 1);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("adsProfileChanged", handleAdsProfileChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adsProfileChanged", handleAdsProfileChange);
    };
  }, [isAdsRoute]);

  const profileIds = useMemo(() => {
    if (isAdsRoute) {
      try {
        const stored = localStorage.getItem("app_v2_ads_profiles");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed as string[];
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to parse stored ads profiles:", error);
      }

      const allIds = adAccounts
        .flatMap((account) => account.profiles)
        .map((profile) => profile.profileId);
      return allIds.length > 0 ? [allIds[0]] : [];
    } else {
      return adAccounts
        .flatMap((account) => account.profiles)
        .filter((profile) => profile.selected)
        .map((profile) => profile.profileId);
    }
  }, [adAccounts, isAdsRoute, localStorageVersion]);

  const requestBody: PortfolioMetricsRequest = useMemo(
    () => ({
      startDate: formatDate(dateRange.from),
      endDate: formatDate(dateRange.to),
      profileIds,
      portfolioIds,
      country: selectedRegion?.countrycode,
    }),
    [dateRange, profileIds, portfolioIds, selectedRegion?.countrycode]
  );

  const enabled = profileIds.length > 0;

  const query = useQuery<PortfolioMetricsResponse, Error>({
    queryKey: [
      PORTFOLIO_QUERY_KEYS.metrics,
      requestBody.startDate,
      requestBody.endDate,
      requestBody.profileIds.join(","),
      (portfolioIds || []).join(","),
      isAdsRoute ? "ads" : "non-ads",
    ],
    queryFn: () => portfolioMetricsService.fetchMetrics(requestBody),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const rows: PortfolioMetricsRow[] = query.data?.data ?? [];

  const kpiData: PortfolioKPIMetric[] = useMemo(() => {
    if (!rows.length) return [];

    const totals = rows.reduce(
      (acc, row) => {
        acc.spend += row.spend || 0;
        acc.impressions += row.impressions || 0;
        acc.sales += row.sales || 0;
        return acc;
      },
      { spend: 0, impressions: 0, sales: 0 }
    );

    const overallAcos =
      totals.sales > 0 ? (totals.spend / totals.sales) * 100 : 0;

    const formatCurrency = (value: number | null | undefined) => {
      if (value == null || Number.isNaN(value)) return "₹0.00";
      const numValue = Number(value);
      if (numValue >= 1_000_000) {
        return `₹${(numValue / 1_000_000).toFixed(2)}M`;
      }
      if (numValue >= 1_000) {
        return `₹${(numValue / 1_000).toFixed(2)}K`;
      }
      return `₹${numValue.toFixed(2)}`;
    };

    const formatNumber = (value: number | null | undefined) => {
      if (value == null || Number.isNaN(value)) return "0";
      const numValue = Number(value);
      if (numValue >= 1_000_000) {
        return `${(numValue / 1_000_000).toFixed(2)}M`;
      }
      if (numValue >= 1_000) {
        return `${(numValue / 1_000).toFixed(2)}K`;
      }
      return numValue.toString();
    };

    const formatPercentage = (value: number | null | undefined) => {
      if (value == null || Number.isNaN(value)) return "0.00%";
      return `${Number(value).toFixed(2)}%`;
    };

    // For now, use simple mock previous values for trend calculations
    const mockPreviousData = {
      spend: totals.spend * 0.95,
      impressions: totals.impressions * 0.88,
      acos: overallAcos * 0.95,
      sales: totals.sales * 0.92,
    };

    const calculateChange = (
      current: number | null | undefined,
      previous: number | null | undefined
    ) => {
      const currentNum = Number(current) || 0;
      const previousNum = Number(previous) || 0;

      if (previousNum === 0) {
        return {
          value: "0.0",
          type: "up" as "up" | "down",
        };
      }

      const change = ((currentNum - previousNum) / previousNum) * 100;
      return {
        value: Math.abs(change).toFixed(1),
        type: (change >= 0 ? "up" : "down") as "up" | "down",
      };
    };

    const adSpendChange = calculateChange(totals.spend, mockPreviousData.spend);
    const impressionsChange = calculateChange(
      totals.impressions,
      mockPreviousData.impressions
    );
    const acosChange = calculateChange(overallAcos, mockPreviousData.acos);
    const salesChange = calculateChange(totals.sales, mockPreviousData.sales);

    return [
      {
        title: "Ad Spend",
        value: formatCurrency(totals.spend),
        change: `${
          adSpendChange.type === "up" ? "+" : "-"
        }${adSpendChange.value}%`,
        changeType: adSpendChange.type,
        subLabel: `from ${formatCurrency(mockPreviousData.spend)}`,
      },
      {
        title: "Impressions",
        value: formatNumber(totals.impressions),
        change: `${
          impressionsChange.type === "up" ? "+" : "-"
        }${impressionsChange.value}%`,
        changeType: impressionsChange.type,
        subLabel: `from ${formatNumber(mockPreviousData.impressions)}`,
      },
      {
        title: "ACoS",
        value: formatPercentage(overallAcos),
        change: `${
          acosChange.type === "up" ? "+" : "-"
        }${acosChange.value}%`,
        changeType: acosChange.type,
        subLabel: `from ${formatPercentage(mockPreviousData.acos)}`,
      },
      {
        title: "Ad Revenue",
        value: formatCurrency(totals.sales),
        change: `${
          salesChange.type === "up" ? "+" : "-"
        }${salesChange.value}%`,
        changeType: salesChange.type,
        subLabel: `from ${formatCurrency(mockPreviousData.sales)}`,
      },
    ];
  }, [rows]);

  return {
    kpiData,
    rows,
    isLoading: query.isLoading,
    error: query.error ?? null,
  };
}

